/**
 * Tax Log V2 API service — READ-ONLY viewer for Tax Planning V2 chat sessions.
 *
 * Promise-based wrapper around the 7 endpoints under `api/v1/TaxLogV2`
 * (TaxLogV2Controller). The axios/base-URL/cookie-auth/unwrap skeleton is copied
 * VERBATIM from `services/taxPlaygroundV2Api.js` (house style of the V2 family) —
 * only `ROOT` and the method list differ, so the V2 services stay diffable.
 *
 * 🔴 GET-ONLY BY CONSTRUCTION. Unlike its siblings this file does NOT define
 * `post`/`put`/`patch`/`del` at all. The Tax Log page must never be able to send
 * a chat turn, reset a form, or write a row — the missing verbs are the
 * service-layer half of that guarantee (the HTTP half is that every route on
 * `TaxLogV2Controller` is `[HttpGet]`). Do not add a mutating helper here; if a
 * caller needs to write, it belongs in `taxPlaygroundV2Api.js` instead.
 *
 * ── The one shape difference from the playground ────────────────────────────
 * The playground keys everything by `sessionId` and assumes ONE form per
 * session. That is false for production chat: a real LHB session restarts the
 * questionnaire and accumulates MANY `tax.TaxFormV2` rows under a single
 * `SessionID` (verified on Local: one session held 32 forms). So Tax Log is
 * keyed by the (FORM × SESSION) PAIR for the list — `listSessions()` returns one
 * row per pair, so that 32-form session shows as 32 rows, each carrying its own
 * `formID`/`status`/`yearCode` — and by SESSION for the transcript (a turn
 * cannot be attributed to a form: `dbo.Log_Message` has no form column).
 * `getSession()` still returns the session's `forms[]` and the viewer picks one
 * (the `?form=` deep link from the list, else newest) to feed `getFormState()`.
 *
 * ── Two turn sources ────────────────────────────────────────────────────────
 * Each turn row carries `source`:
 *   'production' → the turn came from `dbo.Log_Message` (real LHB chat). Its
 *                  detail is fetched with `getProductionTurn(logMessageId)`;
 *                  prompts come from `log.Log_Monitor.ContentRequest`, and there
 *                  is NO per-turn form/taxState snapshot (null on the wire).
 *   'playground' → the turn came from `tax.TaxPlaygroundTurnV2` (Tax Playground
 *                  console). Its detail is fetched with
 *                  `getPlaygroundTurn(turnId)` and DOES carry the per-turn
 *                  `configForm` / `taxState` / `changedPaths` snapshot.
 * Callers must branch on `turn.source`; `turnKey` (`"lm:160195"` / `"pg:812"`)
 * is provided only as a stable v-for key, never as a route parameter.
 *
 * All responses are the standard ResponseModel `{ Message, Status, Data }`; the
 * helpers below unwrap `.Data` and throw on a non-success `Status`
 * (APIStatus.Successful === 0). 404 (missing session/turn, or one belonging to
 * another company — deliberately indistinguishable) surfaces as a thrown Error
 * with `err.httpStatus === 404`.
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth)
// so it does not depend on the global axios default being applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 * Accepts either PascalCase (`Status`/`Data`) or camelCase (`status`/`data`).
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = 'status' in body ? body.status : body.Status;
    const data = 'data' in body ? body.data : body.Data;
    if (status === API_STATUS_SUCCESS) {
      return data;
    }
    const err = new Error(body.message || body.Message || 'Request failed');
    err.status = status;
    err.data = data;
    throw err;
  }
  return body;
}

/** Normalize an axios/transport error into a friendly Error carrying the API message. */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

/** Append only the query params the caller actually set (empty string / null / 0 are dropped). */
function qs(params) {
  const parts = [];
  Object.keys(params || {}).forEach((k) => {
    const v = params[k];
    if (v === null || v === undefined || v === '' || v === false) return;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  });
  return parts.length ? `?${parts.join('&')}` : '';
}

const ROOT = 'api/v1/TaxLogV2';

export const taxLogV2Api = {
  // ── Session list ─────────────────────────────────────────────────────
  /**
   * One row per (FORM × SESSION) PAIR — NOT per session. A session that touched
   * three forms yields three rows, each describing THAT form (`formID`,
   * `status`, `yearCode`, `taxText`, `answeredCount`).
   *
   * 🔴 `turnCount` / `totalTokens` / `firstActivity` / `lastActivity` stay
   * SESSION-level values (`dbo.Log_Message` has no form column, so a turn cannot
   * be attributed to one form) — they repeat identically across every row of the
   * same session. The table labels those columns "ทั้งเซสชัน" for that reason.
   *
   * @param {Object} filter { userId, sessionId, formId, status, yearId, channel,
   *   source: ''|'production'|'playground', from: 'YYYY-MM-DD', to: 'YYYY-MM-DD',
   *   page, pageSize }
   *   ⚠️ `formId` is an exact match; `qs()` drops `0`/`''`/`null` so an empty box
   *   must arrive as `null` (or 0) and simply never reaches the wire.
   * @returns {Promise<Object>} SearchResult<SessionListItem>
   *   { items[], totalCount, page, pageNumber, pageSize, totalPages }
   */
  listSessions(filter) {
    const f = filter || {};
    const formId = Number(f.formId);
    return get(
      `${ROOT}/sessions` +
        qs({
          userId: f.userId,
          sessionId: f.sessionId,
          formId: Number.isFinite(formId) && formId > 0 ? formId : null,
          status: f.status,
          yearId: f.yearId,
          channel: f.channel,
          source: f.source,
          from: f.from,
          to: f.to,
          page: Number(f.page) || 1,
          pageSize: Number(f.pageSize) || 25,
        })
    );
  },

  // ── Session detail (header + every form the session produced) ─────────
  /**
   * @param {string} sessionId
   * @returns {Promise<Object>} { session, forms[] } — `forms` newest first.
   *   404 ⇒ thrown Error with `err.httpStatus === 404`.
   */
  getSession(sessionId) {
    return get(`${ROOT}/sessions/${encodeURIComponent(sessionId ?? '')}`);
  },

  // ── Turn timeline of a session ───────────────────────────────────────
  /**
   * Light rows, oldest → newest (chat reading order). Paging is clamped
   * server-side (`page < 1 → 1`, `pageSize < 1 → 100`, cap 500).
   * @returns {Promise<Object>} SearchResult<TurnSummary>
   */
  listTurns(sessionId, page = 1, pageSize = 100) {
    return get(
      `${ROOT}/sessions/${encodeURIComponent(sessionId ?? '')}/turns` +
        qs({ page: Number(page) || 1, pageSize: Number(pageSize) || 100 })
    );
  },

  // ── Turn detail — branch on `turn.source` ────────────────────────────
  /**
   * Production turn (`source === 'production'`). LLM calls are projected from
   * `log.Log_Monitor` rows linked by `LogMessageID`; `configForm`/`taxState`
   * are null (production keeps no per-turn snapshot).
   * @param {number} logMessageId
   */
  getProductionTurn(logMessageId) {
    return get(`${ROOT}/turns/log/${encodeURIComponent(logMessageId)}`);
  },
  /**
   * Playground turn (`source === 'playground'`) — full per-turn snapshot
   * (`llmCalls` with promptLayers, `configForm`, `taxState`, `changedPaths`).
   * @param {number} turnId
   */
  getPlaygroundTurn(turnId) {
    return get(`${ROOT}/turns/playground/${encodeURIComponent(turnId)}`);
  },

  // ── Form state (feeds the "ฟอร์ม" + "Tax JSON" panels) ────────────────
  /**
   * Current state of ONE form of the session — NOT a per-turn snapshot.
   * @param {number} formId
   * @returns {Promise<Object>} { form, taxState, configForm, progress, versions }
   */
  getFormState(formId) {
    return get(`${ROOT}/forms/${encodeURIComponent(formId)}`);
  },

  // ── Whole-session export (the "ส่งออก JSON" button) ───────────────────
  /**
   * EVERYTHING the viewer can show for one session, in ONE envelope — still a
   * plain GET (reading, not writing: this file stays GET-only).
   * `{ kind: 'lhb.taxlog.v2.session-export', schemaVersion, exportedAt,
   *    exportedBy, source, session, forms[], turns[], turnDetails{turnKey→detail},
   *    formStates{formId→state}, warnings[] }` — each piece has the same shape
   * the per-endpoint methods above return, so the offline page
   * (`utils/taxLogOfflineFile.js → createOfflineTaxLogApi`) can serve it back.
   * ⚠️ Can be tens of MB for a long session; the caller turns it into a Blob
   * download (file name: `buildTaxLogExportFileName`).
   * @param {string} sessionId
   * @returns {Promise<Object>} 404 ⇒ thrown Error with `err.httpStatus === 404`.
   */
  exportSession(sessionId) {
    return get(`${ROOT}/sessions/${encodeURIComponent(sessionId ?? '')}/export`);
  },
};

export default taxLogV2Api;
